import { ExportFormatType, HSDFormatType, NetworkProviderFilterType } from "@app/exports/enumerations";
import { Breadcrumbs, BreadcrumbTemplateEnum, formatDateTime, Page, PageHeader, PageHeaderRight, PageInner, TooltipWrapper } from "@app/shared";
import { QESButton, QESCheckbox, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { FC, useContext, useMemo } from "react";
import { exportTypeToSharedUsersExportTypeMap, getFileFormats, getProvidersReportFormats, ReportTypeNamesMap, reportTypeRouteMap, sharedUsersAggregateTypeMap } from "./helpers";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import ReportCustomizationContextProvider, { ReportCustomizationContext } from "./ReportCustomizationContext";
import { UserContext } from "@app/user/UserContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import SharedUsers from "@app/reports/SharedUsers";
import { ReportCustomizationRunConfiguration } from "./interfaces/reportCustomizationProjectRunConfiguration";
import { ReportCustomizationReducerActions } from "./interfaces/reportCustomizationReducerActionsEnum";
import { Link, useHistory } from "react-router-dom";
import { ReportType } from "./interfaces/reportTypeEnum";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ChangedGapsEnum, ProviderNetworkType } from "@app/projects/enumerators";

export const WrappedReportCustomization: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const user = useContext(UserContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const history = useHistory();
  const showChangedGaps = rcContext.state.reportType === ReportType.SpecialtySummaryByCounty;
  const isChangedGapsDisabled = !rcContext.state.isCompareMode || !rcContext.state.comparedTimeslice;
  const changedGapsTooltip = isChangedGapsDisabled ? "Compare time slices with changed gaps" : "Selecting a Changed Gaps Report will disable service area and specialty filtering";

  const selectedTimeslice = useMemo(() => {
    return rcContext.state.allTimeslices?.find((t) => t.projectRunConfigurationId.toString() === rcContext.state.timeslice);
  }, [rcContext.state.timeslice, rcContext.state.allTimeslices]);

  // Memoize dropdown options
  const reportTypeOptions = useMemo(() => {
    const availableReportTypes = [
      ReportType.AdequacyServicingProvider,
      ReportType.CountySummary,
      ReportType.StateSummary,
      ReportType.AdequacySpecialtySummary,
      ReportType.AdequacyAggregate,
      ReportType.SpecialtySummaryByCounty,
    ];
    if (selectedTimeslice?.showNextClosestProviders) {
      availableReportTypes.push(ReportType.AdequacyNextClosestProvider)
    }
    if (rcContext.routeState.showZipSummariesReport) {
      availableReportTypes.push(ReportType.ZipSummary);
    }

    return availableReportTypes.map((type) => {
      return <option key={type} value={type}>{ReportTypeNamesMap.get(type)}</option>;
    });
  }, [selectedTimeslice?.showNextClosestProviders, rcContext.routeState.showZipSummariesReport]);

  const showMemberships = useMemo(() => {
    return !!selectedTimeslice?.secondaryMemberSetId;
  }, [selectedTimeslice, rcContext.state.allTimeslices]);

  const membershipItems = useMemo(() => {
    const items: DropdownItem<number>[] = [];
    items.push({ key: rcContext.state.allMemberships[0].key, name: rcContext.state.allMemberships[0].name });
    if (rcContext.state.allMemberships.length > 1) {
      items.push({ key: rcContext.state.allMemberships[1].key, name: rcContext.state.allMemberships[1].name });
    }
    return items;
  }, [rcContext.state.allMemberships]);

  const getTimesliceName = (timeslice: ReportCustomizationRunConfiguration) => {
    if (timeslice.name === "Latest Published") {
      return `Latest Published (${formatDateTime(new Date(timeslice.dateCreated))})`;
    } else if (!!timeslice.name) {
      return timeslice.name;
    } else {
      return formatDateTime(new Date(timeslice.dateCreated));
    }
  }

  const primaryTimesliceOptions = useMemo(() => {
    return rcContext.state.allTimeslices.map((option) => {
      // only enable timeslices that share a membership with the compared timeslice
      const comparedConfig = rcContext.state.allTimeslices.find((t) => t.projectRunConfigurationId.toString() === rcContext.state.comparedTimeslice);
      // [undefined].includes(undefined) returns true, so we have to filter to existing ids
      const comparedMemberships = [comparedConfig?.primaryMemberSetId, comparedConfig?.secondaryMemberSetId].filter((m) => !!m);
      const hasMatchingMembership = comparedMemberships.length === 0 || comparedMemberships.includes(option.primaryMemberSetId) || comparedMemberships.includes(option.secondaryMemberSetId);
      return (
        <option
          key={option.projectRunConfigurationId}
          value={option.projectRunConfigurationId}
          disabled={option.projectRunConfigurationId.toString() === rcContext.state.comparedTimeslice || !hasMatchingMembership}
        >
          {getTimesliceName(option)}
        </option>
      );
    });
  }, [rcContext.state.allTimeslices, rcContext.state.comparedTimeslice, rcContext.state.selectedMemberships]);

  const secondaryTimesliceOptions = useMemo(() => {
    return rcContext.state.allTimeslices.map((option) => {
      // only enable timeslices that include the currently selected membership
      // there's only one membership now, otherwise we wouldn't have compare mode
      const hasMatchingMembership = rcContext.state.selectedMemberships.length > 0 && option.primaryMemberSetId === rcContext.state.selectedMemberships[0]?.key || (!!option.secondaryMemberSetId && option.secondaryMemberSetId === rcContext.state.selectedMemberships[0]?.key);
      return (
        <option
          key={option.projectRunConfigurationId}
          value={option.projectRunConfigurationId}
          disabled={option.projectRunConfigurationId.toString() === rcContext.state.timeslice || !hasMatchingMembership}
        >
          {getTimesliceName(option)}
        </option>
      );
    });
  }, [rcContext.state.allTimeslices, rcContext.state.timeslice, rcContext.state.selectedMemberships]);

  const fileFormatOptions = useMemo(() => {
    const formats = getFileFormats(rcContext.routeState.exportNetworkProviderCount > rcContext.excelRecordsLimit, rcContext.state.reportType);
    return formats.map((f) => <option key={f.key} value={f.key}>{f.value}</option>);
  }, [rcContext.routeState.exportNetworkProviderCount, rcContext.state.reportType]);

  const reportFormatOptions = useMemo(() => {
    const formats = getProvidersReportFormats(user.roles, featureFlags, rcContext.state.providerSource as ProviderNetworkType);
    return formats.map((f) => <option key={f.key} value={f.key}>{f.value}</option>);
  }, [user.roles, featureFlags, rcContext.state.providerSource]);

  const reportView = useMemo(() => {
    if (reportTypeRouteMap.has(rcContext.state.reportType)) {
      return reportTypeRouteMap.get(rcContext.state.reportType);
    }
  }, [rcContext.state.reportType]);

  return (
    <Page documentTitle="Create Downloadable Report">
      <Breadcrumbs
        template={BreadcrumbTemplateEnum.NetworkAssessment}
        items={[
          { link: `/projects/${rcContext.routeParams.projectId}`, text: rcContext.routeState.projectName },
          rcContext.routeParams.module.charAt(0).toUpperCase() + rcContext.routeParams.module.slice(1),
          ...(rcContext.routeState.filters?.map(m => m.displayText) || [])
          , "Create Report"]}
      />
      <PageInner>
        <PageHeader title="Report Details" disableHR={true}>
          <PageHeaderRight>
            {!!rcContext.routeState.projectName && <QESButton qesType="secondary" onClick={history.goBack} qesTestId="report-customization__back">Return to Project</QESButton>}
            <Link to="/reports" className="qes-button qes-button-primary">Reports</Link>
          </PageHeaderRight>
        </PageHeader>
        <div className="container ml-0">
          <div className="row mb-2">
            <QESSelect
              containerClassName="col-4"
              label="Report Type"
              value={rcContext.state.reportType}
              onChange={(e) => rcContext.changeReportType(e.target.value as ReportType)}
              testId="report-customization__report-type"
              required={true}
              isValid={!!rcContext.state.reportType}
            >
              {reportTypeOptions}
            </QESSelect>

            {showMemberships && (
              <MultiselectDropdown
                items={membershipItems}
                label={
                  <>
                    {rcContext.state.allTimeslices.length > 1 && (
                      <TooltipWrapper text="Membership comparison is not available while comparing multiple time slices" containerClassName="mr-1">
                        <QesIcon iconName={QesIcons.FontAwesome.InfoCircle} description="hover or focus to show tooltip" width="12" svgStyle={{ marginBottom: "2px" }} />
                      </TooltipWrapper>
                    )}
                    Membership
                  </>
                }
                selectedItems={rcContext.state.selectedMemberships}
                testId="report-customization__membership"
                containerClassName="col-4"
                hideSearch={true}
                hideSelectAll={true}
                disabled={rcContext.state.isCompareMode || !!rcContext.state.comparedTimeslice}
                isValid={rcContext.state.reportType === ReportType.AdequacyNextClosestProvider ? rcContext.state.selectedMemberships.length === 1 : rcContext.state.selectedMemberships.length > 0}
                required={true}
                customSelectInputText={rcContext.state.selectedMemberships.length === 2 ? "Both" : rcContext.state.selectedMemberships.length === 0 ? "None" : rcContext.state.selectedMemberships[0].name}
                onSelectionChanged={(newMemberships) => rcContext.changeMemberships(newMemberships)}
              />
            )}
          </div>

          {/* Stuff that's in every report */}
          <>
            <div className="row mb-2">
              <QESSelect
                containerClassName="col-4"
                label="Timeslice"
                value={rcContext.state.timeslice}
                onChange={(e) => rcContext.changeTimeslice(e.target.value, false)}
                testId="report-customization__timeslice"
                disabled={primaryTimesliceOptions.length === 0}
                required={true}
                isValid={!!rcContext.state.timeslice}
              >
                {primaryTimesliceOptions}
              </QESSelect>

              {rcContext.state.allTimeslices.length > 1 && rcContext.routeState.showAdvancedComparison && rcContext.state.reportType !== ReportType.AdequacyNextClosestProvider && (
                <>
                  <QESCheckbox
                    containerClassName="col-auto align-self-end"
                    label="Compare to"
                    checked={rcContext.state.isCompareMode}
                    onChange={(e) => rcContext.changeIsCompareMode(e.target.checked)}
                    testId="report-customization__compare-to"
                    disabled={rcContext.state.selectedMemberships.length > 1}
                  />

                  <QESSelect
                    containerClassName="col-4"
                    label="Timeslice"
                    value={rcContext.state.comparedTimeslice}
                    onChange={(e) => rcContext.changeTimeslice(e.target.value, true)}
                    testId="report-customization__compared-timeslice"
                    disabled={!rcContext.state.isCompareMode || secondaryTimesliceOptions.length === 0}
                  >
                    {secondaryTimesliceOptions}
                  </QESSelect>

                  {showChangedGaps && (
                    <QESSelect
                      containerClassName="col-2"
                      label={
                        <>
                          <TooltipWrapper text={changedGapsTooltip} containerClassName="mr-1">
                            <QesIcon iconName={QesIcons.FontAwesome.InfoCircle} description="hover or focus to show tooltip" width="12" svgStyle={{ marginBottom: "2px" }} />
                          </TooltipWrapper>
                          Changed Gaps
                        </>
                      }
                      value={rcContext.state.changedGapsType}
                      onChange={(e) => rcContext.changeChangedGapsType(e.target.value as ChangedGapsEnum || "")}
                      testId="report-customization__changed-gaps"
                      disabled={isChangedGapsDisabled}
                    >
                      <option value=""></option>
                      <option value={ChangedGapsEnum.All}>All Changed Gaps</option>
                      <option value={ChangedGapsEnum.Opened}>Opened Gaps</option>
                      <option value={ChangedGapsEnum.Closed}>Closed Gaps</option>
                    </QESSelect>
                  )}
                </>
              )}
            </div>

            <div className="row mb-2">
              <QESTextInput
                containerClassName="col-5"
                label="File Name"
                testId="report-customization__file-name"
                value={rcContext.state.filename}
                onChange={(e) => rcContext.dispatch({ payload: { filename: e.target.value }, type: ReportCustomizationReducerActions.ChangeFilename })}
                required={true}
                isValid={!!rcContext.state.filename}
              />

              <QESSelect
                containerClassName="col-4"
                label="File Format"
                value={rcContext.state.fileFormat}
                onChange={(e) => rcContext.changeFileFormat(e.target.value as ExportFormatType)}
                testId="report-customization__file-format"
                required={true}
                isValid={!!rcContext.state.fileFormat}
              >
                {fileFormatOptions}
              </QESSelect>

              {rcContext.state.reportType === ReportType.AdequacyServicingProvider
                && (
                  <QESSelect
                    containerClassName="col-3"
                    label="Report Format"
                    value={rcContext.state.reportFormat}
                    onChange={(e) => rcContext.changeReportFormat(e.target.value as HSDFormatType)}
                    testId="report-customization__report-format"
                    disabled={rcContext.state.fileFormat === ExportFormatType.Excel || rcContext.state.fileFormat === ExportFormatType.Pipe}
                    required={rcContext.state.reportType === ReportType.AdequacyServicingProvider}
                    isValid={!!rcContext.state.reportType}
                  >
                    {reportFormatOptions}
                  </QESSelect>
                )}
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <SharedUsers
                  exportType={exportTypeToSharedUsersExportTypeMap.get(rcContext.state.reportType)}
                  customProjectIds={[parseInt(rcContext.routeParams.projectId)]}
                  tooltipTitle="Users in this list have been filtered to only show users with access to this project and information in this report"
                  networkProviderFilterType={rcContext.state.providerFilter as NetworkProviderFilterType}
                  aggregateType={sharedUsersAggregateTypeMap.get(rcContext.state.reportType)}
                  updateSelectedSharedUsers={(newSharedUsers) => rcContext.dispatch({ payload: { sharedUsers: newSharedUsers }, type: ReportCustomizationReducerActions.ChangeSharedUsers })}
                  sharedUsersFilter={rcContext.state.sharedUsers}
                  containerClassnames="d-block"
                />
              </div>

              <QESCheckbox
                containerClassName="col-auto align-self-end"
                label="Email me a link to the report once it's ready."
                checked={rcContext.state.shouldEmailLink}
                onChange={(e) => rcContext.dispatch({ payload: { shouldEmailLink: e.target.checked }, type: ReportCustomizationReducerActions.ChangeShouldEmailLink })}
                testId="report-customization__should-email-link"
              />
            </div>
          </>
        </div>

        {reportView}
      </PageInner>
    </Page>
  )
}

export const ReportCustomization: FC = () => {
  return (
    <ReportCustomizationContextProvider>
      <WrappedReportCustomization />
    </ReportCustomizationContextProvider>
  );
};
